import { jsx } from "react/jsx-runtime";
import { createContext, useMemo, useCallback, useState, useRef, useEffect, Suspense, useContext } from "react";
import { useNavigate, useHref, Route, Routes, useSearchParams } from "react-router";
import { Funnel } from "@airhelp/funnel-core";
import { F as FunnelContextProvider, u as useFunnel } from "./react-D5l6P7RL.js";
const FunnelStepContext = createContext(null);
function ReactRouterFunnel({
  definition,
  loadingIndicator,
  notFoundStep,
  logLevel
}) {
  const navigate = useNavigate();
  const basename = useHref("/");
  const basenamePrefix = basename.endsWith("/") ? basename.substring(0, -1) : basename;
  const logger = useMemo(
    () => ({
      info: (line, ...data) => {
        console.debug(`[FUNNEL DEBUG] ${line}`, ...data);
      },
      // eslint-disable-next-line no-console -- Debug log
      table: console.table
    }),
    []
  );
  const debug = useCallback(
    (callback) => {
      logLevel === "debug" && callback(logger);
    },
    [logLevel, logger]
  );
  const [funnelStep, setFunnelStep] = useState(null);
  const onStepChange = useCallback(
    async (step, event) => {
      const stepDefinitionUri = `${definition.basePath ?? ""}${(step == null ? void 0 : step.uri) ?? ""}`;
      const stepBrowserUri = `${basenamePrefix}${stepDefinitionUri}`;
      const stepPathname = new URL(stepBrowserUri, window.location.href).pathname;
      const stepParams = new URL(stepDefinitionUri, window.location.href).searchParams;
      const windowParams = new URLSearchParams(window.location.search);
      const stepParamsMatch = Array.from(stepParams.entries()).every(
        ([key, value]) => windowParams.get(key) === value
      );
      debug(({ info, table }) => {
        info("Step change initiated");
        table({
          stepDefinitionUri,
          stepBrowserUri,
          stepPathname,
          stepParams,
          windowParams,
          stepParamsMatch,
          willNavigate: stepPathname !== window.location.pathname || !stepParamsMatch
        });
      });
      setFunnelStep(step);
      if (stepPathname !== window.location.pathname || !stepParamsMatch) {
        event.changedFromDisabledStep ? await navigate(stepDefinitionUri, { replace: true }) : await navigate(stepDefinitionUri);
      }
      if (definition.onStepChange) {
        definition.onStepChange(step, event);
      }
    },
    [basenamePrefix, debug, definition, navigate]
  );
  const funnelRef = useRef(
    new Funnel(
      {
        ...definition,
        basePath: `${basenamePrefix}${definition.basePath ?? ""}`,
        onStepChange,
        resolveLocation: definition.resolveLocation ?? (() => new URL(window.location.toString()))
      },
      {
        logLevel,
        logger
      }
    )
  );
  const stepsByUriAndParams = useMemo(() => {
    const record = {};
    definition.steps.list.forEach((step) => {
      const stepUrl = new URL(step.uri ?? "", "http://__internal_domain__.com");
      const stepParams = stepUrl.searchParams;
      if (typeof record[stepUrl.pathname] === "undefined") {
        record[stepUrl.pathname] = [{ step, params: stepParams }];
      } else {
        record[stepUrl.pathname].push({ step, params: stepParams });
      }
    });
    return record;
  }, [definition.steps.list]);
  const routes = useMemo(() => {
    return Object.keys(stepsByUriAndParams).map((path) => {
      if (stepsByUriAndParams[path].length === 1) {
        const { step } = stepsByUriAndParams[path][0];
        return /* @__PURE__ */ jsx(
          Route,
          {
            element: /* @__PURE__ */ jsx(FunnelStep, { onStepEnter: definition.onStepEnter, step }),
            path
          },
          path
        );
      }
      return /* @__PURE__ */ jsx(
        Route,
        {
          element: /* @__PURE__ */ jsx(
            ParametrisedFunnelStep,
            {
              notFoundStep,
              onStepEnter: definition.onStepEnter,
              pathSteps: stepsByUriAndParams[path]
            }
          ),
          path
        },
        path
      );
    });
  }, [definition.onStepEnter, notFoundStep, stepsByUriAndParams]);
  useEffect(() => {
    if (funnelRef.current.currentStep === null) {
      debug(({ info }) => {
        info("Funnel is initiating for the first time, detecting browser step");
      });
      funnelRef.current.detectBrowserStep();
    }
  }, []);
  return /* @__PURE__ */ jsx(Suspense, { fallback: loadingIndicator, children: /* @__PURE__ */ jsx(FunnelContextProvider, { funnel: funnelRef.current, children: /* @__PURE__ */ jsx(FunnelStepContext.Provider, { value: funnelStep, children: /* @__PURE__ */ jsx(Routes, { children: routes }) }) }) });
}
function ParametrisedFunnelStep({
  onStepEnter,
  pathSteps,
  notFoundStep
}) {
  const [searchParams] = useSearchParams();
  const matchedStep = useMemo(() => {
    const matched = pathSteps.find((item) => {
      const stepParams = item.params;
      for (const [key, value] of searchParams.entries()) {
        if (stepParams.get(key) !== value) {
          return false;
        }
      }
      return true;
    });
    return (matched == null ? void 0 : matched.step) ?? null;
  }, [searchParams, pathSteps]);
  if (!matchedStep) {
    return notFoundStep;
  }
  return /* @__PURE__ */ jsx(FunnelStep, { onStepEnter, step: matchedStep });
}
function FunnelStep({
  onStepEnter,
  step
}) {
  const funnel = useFunnel();
  const funnelStep = useFunnelCurrentStep();
  const [didInit, setDidInit] = useState(false);
  const browserStep = funnel.peekBrowserStep();
  const isStepEnabled = (browserStep == null ? void 0 : browserStep.name) ? funnel.peekStep(browserStep.name).enabled : false;
  useEffect(() => {
    if (browserStep && isStepEnabled && (funnelStep == null ? void 0 : funnelStep.name) !== browserStep.name) {
      funnel.forceStep(browserStep.name);
    }
  }, [funnel, funnelStep, browserStep, isStepEnabled]);
  useEffect(() => {
    if (!didInit) {
      onStepEnter && browserStep && onStepEnter(browserStep);
      setDidInit(true);
    }
  }, [didInit, browserStep, onStepEnter]);
  useEffect(() => {
    if (!isStepEnabled) {
      funnel.detectBrowserStep();
    }
  }, [funnel, isStepEnabled]);
  if (!step.Component) {
    throw new Error(
      `Funnel load error, funnel step should always have a component! Occurred at step: ${step.name}`
    );
  }
  if (!isStepEnabled || !didInit) {
    return null;
  }
  return /* @__PURE__ */ jsx(step.Component, {});
}
function useFunnelInit(builder) {
  const isInitialised = useRef(false);
  const definitionRef = useRef(
    null
  );
  if (!isInitialised.current) {
    const { onInit, ...definition } = builder();
    definitionRef.current = definition;
    onInit == null ? void 0 : onInit();
    isInitialised.current = true;
  }
  return definitionRef.current;
}
function useFunnelCurrentStep() {
  return useContext(FunnelStepContext);
}
export {
  ReactRouterFunnel,
  useFunnelCurrentStep,
  useFunnelInit
};
//# sourceMappingURL=react-router.js.map
